import {
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useInsertionEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';

import type { MapClusterPin, MapPin } from './pins';
import { formatClusterCount, formatPinPrice } from './pins';

/*
 * Shared SVG markup keeps the street map's overlay engine and the globe's React
 * markers consistent. Mobile count bubbles match the native apps' circular shape.
 */

/** Bubble diameter steps up with the count, from the iOS ClusterSizeCalculator. */
const clusterDiameter = (count: number): number => {
  if (count < 10) return 44;
  if (count < 50) return 52;
  if (count < 100) return 60;
  if (count < 500) return 68;
  return 76;
};

const clusterCountFontSize = (count: number): number => {
  if (count >= 500) return 24;
  if (count >= 50) return 22;
  return 20;
};

const FONT_FAMILY = 'Graphik, system-ui, sans-serif';
const PIN_STROKE = '#D7D4CD';
const PIN_VIEWED = '#A4A19B';
const PIN_INK = '#231E1B';
const PIN_INNER = '#0266F2';
const PIN_HEART = '#F357AE';
const BRAND_BLUE = 'var(--color-od-content-accent-brand)';
const NEUTRAL_SHADOW = 'drop-shadow(0 2px 3px rgba(137, 134, 127, 0.15))';
const BLUE_SHADOW = 'drop-shadow(0 2px 3px rgba(0, 64, 230, 0.15))';

export const PIN_BUBBLE_HEIGHT = 33.5;
/** Distance from the top of the pin to its tail tip, which sits on the coordinate. */
export const PIN_TAIL_TIP = 38.57;
/** Half the width of the tail where it meets the bubble. */
const PIN_TAIL_HALF_WIDTH = 8.9571;
/** Puts the pin's tail tip on the element's origin. */
export const PIN_ANCHOR = `translate(-50%, -${PIN_TAIL_TIP}px)`;
export const PIN_ORIGIN = `50% ${PIN_TAIL_TIP}px`;

/**
 * The pin's resting look. Selection is the `odmap-selected` class on an ancestor, so the mounted
 * artwork can animate between the looks.
 */
export type PinVariant = 'default' | 'viewed' | 'fav-viewed';

const PIN_VARIANTS: Record<PinVariant, { heart: boolean; label: string }> = {
  default: { heart: false, label: PIN_INK },
  viewed: { heart: false, label: PIN_VIEWED },
  'fav-viewed': { heart: true, label: PIN_VIEWED },
};

export const PIN_SELECTED_CLASS = 'odmap-selected';
/** Street-map marker holding a price pin; the marker, not its artwork, is the pin's hit area. */
export const PIN_MARKER_CLASS = 'odmap-pin-marker';

export const pinVariant = ({ liked, viewed }: { liked: boolean; viewed: boolean }): PinVariant => {
  if (liked) return 'fav-viewed';
  return viewed ? 'viewed' : 'default';
};

let measureContext: CanvasRenderingContext2D | null | undefined;
const measureText = (text: string, font: string, letterSpacing: string) => {
  measureContext ??= document.createElement('canvas').getContext('2d');
  if (!measureContext) return null;
  measureContext.font = font;
  measureContext.letterSpacing = letterSpacing;
  return measureContext.measureText(text);
};

/** Shared by the label and the probe that measures it, so both lay out the same. */
const PIN_LABEL_TEXT = `white-space:nowrap;font:500 14px/17.5px ${FONT_FAMILY};letter-spacing:-0.014em;font-variant-numeric:tabular-nums`;

/**
 * Canvas text metrics cannot apply tabular figures, so labels are measured as
 * the page lays them out, all in one layout pass. A label the page cannot lay
 * out is left out.
 */
const layOutLabels = (prices: Iterable<string>) => {
  const widths = new Map<string, number>();
  if (typeof document === 'undefined' || !document.body) return widths;
  const probes = [...prices].map((price) => {
    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = `position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;${PIN_LABEL_TEXT}`;
    probe.textContent = price;
    return { price, probe };
  });
  document.body.append(...probes.map(({ probe }) => probe));
  for (const { price, probe } of probes) {
    const { width } = probe.getBoundingClientRect();
    if (width > 0) widths.set(price, width);
  }
  for (const { probe } of probes) probe.remove();
  return widths;
};

/** Price label widths measured with the fonts loaded so far. */
export type PinLabelMetrics = { readonly widths: Map<string, number> };

let labelMetrics: PinLabelMetrics = { widths: new Map() };
const labelMetricsListeners = new Set<() => void>();
let watchingFonts = false;

/** Publishes new widths only when a font load changed a measured label, so other fonts cause no rebuild. */
const remeasureLabels = () => {
  const widths = layOutLabels(labelMetrics.widths.keys());
  const changed = [...labelMetrics.widths].some(([price, width]) => widths.get(price) !== width);
  if (!changed) return;
  labelMetrics = { widths };
  labelMetricsListeners.forEach((listener) => listener());
};

/**
 * A label measured before the web font swaps in has the fallback font's width.
 * `loadingdone` fires when the first fonts finish loading and after every later load.
 */
const watchFonts = () => {
  if (watchingFonts || typeof document === 'undefined' || !document.fonts) return;
  watchingFonts = true;
  document.fonts.addEventListener('loadingdone', remeasureLabels);
};

const labelWidth = (price: string, metrics: PinLabelMetrics) => {
  watchFonts();
  const cached = metrics.widths.get(price);
  if (cached !== undefined) return cached;
  const laidOut = layOutLabels([price]).get(price);
  if (laidOut === undefined)
    return measureText(price, `500 14px ${FONT_FAMILY}`, '-0.196px')?.width ?? price.length * 8;
  metrics.widths.set(price, laidOut);
  return laidOut;
};

const getLabelMetrics = () => labelMetrics;
const subscribeToLabelMetrics = (listener: () => void) => {
  labelMetricsListeners.add(listener);
  watchFonts();
  return () => {
    labelMetricsListeners.delete(listener);
  };
};

/** Replaced after a web font finishes loading, so price pin markup built from it is rebuilt. */
export const usePinLabelMetrics = () =>
  useSyncExternalStore(subscribeToLabelMetrics, getLabelMetrics, getLabelMetrics);

/** Bubble width: the label with 12px of padding on each side, plus the heart and its gap. */
export const pricePinWidth = (pin: MapPin, variant: PinVariant, metrics = labelMetrics) =>
  Math.round(
    12 +
      (PIN_VARIANTS[variant].heart ? 24 : 0) +
      labelWidth(formatPinPrice(pin.listPriceCents), metrics) +
      12,
  );

const pinPath = (width: number) => {
  const margin = 3;
  const radius = 12;
  const k = 6.6274;
  const left = margin;
  const right = margin + width;
  const top = 1;
  const bottom = top + PIN_BUBBLE_HEIGHT;
  const cx = margin + width / 2;
  const t1 = bottom + 2.5357;
  const t2 = bottom + 5.9167;
  const t3 = bottom + 0.9626;
  return [
    `M${cx + 4.4786},${t1}`,
    `C${cx + 2.45},${t2} ${cx - 2.45},${t2} ${cx - 4.4786},${t1}`,
    `C${cx - 5.4225},${t3} ${cx - 7.1225},${bottom} ${cx - PIN_TAIL_HALF_WIDTH},${bottom}`,
    `H${left + radius}`,
    `C${left + radius - k},${bottom} ${left},${bottom - radius + k} ${left},${bottom - radius}`,
    `V${top + radius}`,
    `C${left},${top + radius - k} ${left + radius - k},${top} ${left + radius},${top}`,
    `H${right - radius}`,
    `C${right - radius + k},${top} ${right},${top + radius - k} ${right},${top + radius}`,
    `V${bottom - radius}`,
    `C${right},${bottom - radius + k} ${right - radius + k},${bottom} ${right - radius},${bottom}`,
    `H${cx + PIN_TAIL_HALF_WIDTH}`,
    `C${cx + 7.1225},${bottom} ${cx + 5.4225},${t3} ${cx + 4.4786},${t1}`,
    'Z',
  ].join(' ');
};

const HEART_PATH =
  'M5.14771 2.72367C5.80575 2.51551 6.50049 2.44636 7.18677 2.51956C7.8729 2.59285 8.53681 2.80721 9.13599 3.14945C9.44747 3.32741 9.73812 3.53848 10.0032 3.77738C10.2683 3.54102 10.5586 3.33302 10.8694 3.15726C11.4683 2.81858 12.1313 2.60709 12.8157 2.53617C13.4998 2.46537 14.1915 2.53655 14.8469 2.74515C15.5025 2.95386 16.1088 3.29567 16.6262 3.74906C17.1434 4.20244 17.5619 4.75855 17.8547 5.38089C18.1475 6.00325 18.3088 6.67972 18.3284 7.36722C18.3479 8.05491 18.2251 8.73983 17.968 9.37796C17.7109 10.016 17.3249 10.5942 16.8342 11.0762C16.8214 11.0889 16.8067 11.0997 16.7932 11.1114L10.5872 17.2588C10.2625 17.5803 9.73911 17.5801 9.41431 17.2588L3.16431 11.0694C2.67234 10.5892 2.2838 10.0119 2.02466 9.37503C1.76464 8.73582 1.63938 8.04925 1.65748 7.35941C1.67565 6.66946 1.83679 5.99002 2.13013 5.36527C2.42352 4.74057 2.84327 4.18316 3.36256 3.72855C3.88182 3.27398 4.48974 2.93186 5.14771 2.72367Z';

/** Stable SVG ids per artwork so re-rendering the same pin does not churn its markup. */
const artworkId = (scope: string, key: string) => {
  let hash = 5381;
  for (let i = 0; i < key.length; i++) hash = ((hash << 5) + hash + key.charCodeAt(i)) >>> 0;
  return `odmap-${scope}-${hash.toString(36)}`;
};

/** Draws the resting pin with its blue selected layer hidden on top, for the styles to reveal. */
export const pricePinMarkup = (
  pin: MapPin,
  variant: PinVariant,
  scope: string,
  metrics = labelMetrics,
) => {
  const price = formatPinPrice(pin.listPriceCents);
  const { heart, label } = PIN_VARIANTS[variant];
  const width = pricePinWidth(pin, variant, metrics);
  const filterId = artworkId(`${scope}-pin`, pin.addressId);
  const path = pinPath(width);
  const layer = (name: string, shadow: string, body: string) =>
    `<svg class="odmap-pin-${name}" aria-hidden="true" width="${width + 6}" height="48" viewBox="0 0 ${width + 6} 48" style="position:absolute;top:-1px;left:-3px;pointer-events:none;filter:${shadow}">${body}</svg>`;
  return `<div class="odmap-pin" style="--odmap-pin-ink:${label};position:relative;display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;width:${width}px;height:${PIN_BUBBLE_HEIGHT}px;padding:0 12px;gap:4px">
    ${layer('rest', NEUTRAL_SHADOW, `<path d="${path}" style="fill:#FFFFFF;stroke:${PIN_STROKE};stroke-width:1"/>`)}
    ${layer(
      'active',
      BLUE_SHADOW,
      `<defs><filter id="${filterId}" x="-20%" y="-20%" width="140%" height="140%">
        <feOffset dy="2"/><feGaussianBlur stdDeviation="1" result="ob"/>
        <feComposite operator="out" in="SourceGraphic" in2="ob" result="inv"/>
        <feFlood flood-color="${PIN_INNER}" result="c"/>
        <feComposite operator="in" in="c" in2="inv" result="sh"/>
        <feComposite operator="over" in="sh" in2="SourceGraphic"/>
      </filter></defs>
      <path d="${path}" style="fill:${BRAND_BLUE}" filter="url(#${filterId})"/>`,
    )}
    ${heart ? `<svg aria-hidden="true" viewBox="0 0 20 20" style="position:relative;width:20px;height:20px;flex:none;fill:${PIN_HEART}"><path d="${HEART_PATH}"/></svg>` : ''}
    <span class="odmap-pin-label" style="position:relative;${PIN_LABEL_TEXT}">${price}</span>
  </div>`;
};

const CLUSTER_SHAPE =
  'M43 1C65.0914 1 83 18.9086 83 41C83 56.6295 74.0348 70.1629 60.9674 76.7441C59.8222 77.3209 58.785 78.1018 57.984 79.103L49.9137 89.1913C46.7112 93.1946 40.6226 93.1947 37.4199 89.1915L30.1474 80.1014C29.2549 78.9858 28.0717 78.1463 26.7663 77.5658C12.7644 71.3406 3 57.3116 3 41C3.00002 18.9086 20.9086 1 43 1Z';
/** The anchor is the pin's tail tip or the circle's bottom edge. */
export const CLUSTER_BOXES = {
  pin: { w: 86, h: 98, circle: 80, cx: 43, cy: 41, anchorX: 43.667, anchorY: 92.194 },
  circle: { w: 86, h: 86, circle: 80, cx: 43, cy: 43, anchorX: 43, anchorY: 83 },
};
export type ClusterShape = keyof typeof CLUSTER_BOXES;
export const CLUSTER_STROKE = '#D7D4CD';

export const clusterScale = (count: number) => clusterDiameter(count) / CLUSTER_BOXES.pin.circle;

/** Bubble artwork whose margins put its anchor on the element's origin. */
export const clusterMarkup = (
  cluster: Pick<MapClusterPin, 'id' | 'count'>,
  scope: string,
  shape: ClusterShape = 'pin',
) => {
  const { count } = cluster;
  const box = CLUSTER_BOXES[shape];
  const scale = clusterScale(count);
  const width = box.w * scale;
  const height = box.h * scale;
  const fontSize = clusterCountFontSize(count);
  const label = formatClusterCount(count);
  const metrics = measureText(label, `500 ${fontSize}px ${FONT_FAMILY}`, '0px');
  const opticalHeight = metrics
    ? metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent
    : fontSize * 0.7;
  const baseline = box.cy + opticalHeight / (2 * scale);
  const clipId = artworkId(`${scope}-cluster`, `${cluster.id}:${count}`);
  const body =
    shape === 'circle'
      ? `<circle cx="${box.cx}" cy="${box.cy}" r="${box.circle / 2 - 0.5 / scale}" fill="#FFFFFF" stroke="${CLUSTER_STROKE}" stroke-width="${1 / scale}"/>`
      : `<defs><clipPath id="${clipId}"><path d="${CLUSTER_SHAPE}"/></clipPath></defs>
         <g clip-path="url(#${clipId})"><path d="${CLUSTER_SHAPE}" fill="#FFFFFF" stroke="${CLUSTER_STROKE}" stroke-width="${2 / scale}"/></g>`;
  return `<div class="odmap-cluster" style="position:relative;display:block;width:${width}px;height:${height}px;margin:-${box.anchorY * scale}px 0 0 -${box.anchorX * scale}px;transform-origin:${box.cx * scale}px ${box.cy * scale}px">
    <svg aria-hidden="true" width="${width}" height="${height}" viewBox="0 0 ${box.w} ${box.h}" style="display:block;overflow:visible;filter:${NEUTRAL_SHADOW}">
      ${body}
      <text x="${box.cx}" y="${baseline}" text-anchor="middle" font-size="${fontSize / scale}" font-family="${FONT_FAMILY}" font-weight="500" fill="#000000" style="font-variant-numeric:tabular-nums">${label}</text>
    </svg>
  </div>`;
};

const SELECT_MS = 150;

/**
 * Pressed bubbles shrink slightly; items fade in when they enter the viewport. A selected price
 * pin fades to blue and grows from its tail tip. The resting layer hides only once the blue layer
 * covers it, and returns at once when deselected, so the pin never turns see-through mid-fade.
 * A deselected marker keeps its stack order until the fade ends, so a neighbor does not cover it.
 * A price pin's hit area is its marker box plus a strip under the bubble only as wide as the tail,
 * so it does not cover the top edge of an overlapping pin below. The artwork inside grows on hover,
 * so letting it take the pointer would move the hover edge under a still cursor.
 */
const ARTWORK_STYLES = `
  .odmap-marker { transition: z-index 0s ${SELECT_MS}ms; }
  .odmap-marker.${PIN_SELECTED_CLASS} { transition: none; }
  .${PIN_MARKER_CLASS}::after {
    content: '';
    position: absolute;
    top: ${PIN_BUBBLE_HEIGHT}px;
    left: 50%;
    width: ${(2 * PIN_TAIL_HALF_WIDTH).toFixed(2)}px;
    height: ${(PIN_TAIL_TIP - PIN_BUBBLE_HEIGHT).toFixed(2)}px;
    margin-left: -${PIN_TAIL_HALF_WIDTH.toFixed(2)}px;
  }
  .${PIN_MARKER_CLASS} .odmap-pin { pointer-events: none; }
  .odmap-cluster { transition: transform 150ms ease-out; }
  .odmap-cluster:active { transform: scale(0.96); transition-duration: 100ms; }
  @keyframes odmap-appear { from { opacity: 0; } to { opacity: 1; } }
  .odmap-appear { animation: odmap-appear 120ms ease; }
  .odmap-pin { transform-origin: ${PIN_ORIGIN}; transition: transform ${SELECT_MS}ms ease-out; }
  .odmap-pin-rest { transition: opacity 0s; }
  .odmap-pin-active {
    opacity: 0;
    visibility: hidden;
    transition: opacity ${SELECT_MS}ms ease-out, visibility 0s ${SELECT_MS}ms;
  }
  .odmap-pin-label { color: var(--odmap-pin-ink); transition: color ${SELECT_MS}ms ease-out; }
  .${PIN_SELECTED_CLASS} .odmap-pin { transform: scale(1.06); }
  .${PIN_SELECTED_CLASS} .odmap-pin-rest { opacity: 0; transition: opacity 0s ${SELECT_MS}ms; }
  .${PIN_SELECTED_CLASS} .odmap-pin-active {
    opacity: 1;
    visibility: inherit;
    transition: opacity ${SELECT_MS}ms ease-out, visibility 0s;
  }
  .${PIN_SELECTED_CLASS} .odmap-pin-label { color: #FFFFFF; }
  @media (prefers-reduced-motion: reduce) {
    .odmap-marker { transition: none; }
    .odmap-cluster, .odmap-appear { transition: none; animation: none; }
    .odmap-pin, .odmap-pin-rest, .odmap-pin-active, .odmap-pin-label,
    .${PIN_SELECTED_CLASS} .odmap-pin-rest, .${PIN_SELECTED_CLASS} .odmap-pin-active {
      transition: none;
    }
    .${PIN_SELECTED_CLASS} .odmap-pin { transform: none; }
  }
`;
const STYLE_ELEMENT_ID = 'odmap-artwork-styles';
export const ensureArtworkStyles = () => {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = ARTWORK_STYLES;
  document.head.appendChild(style);
};

export const mapPinLabel = (pin: MapPin, viewed = false) => {
  const visiblePrice = formatPinPrice(pin.listPriceCents);
  const priceLabel = viewed ? `${visiblePrice}, previously viewed` : visiblePrice;
  let price = viewed ? `${priceLabel}, call for price` : 'Call for price';
  if (pin.listPriceCents == null) price = `${priceLabel}, price not available`;
  else if (pin.listPriceCents > 0)
    price = `${priceLabel}, listed at $${(pin.listPriceCents / 100).toLocaleString('en-US')}`;
  return pin.isLiked ? `${price}, saved` : price;
};

export const mapPinButtonLabel = (pin: MapPin, viewed = false) =>
  `${mapPinLabel(pin, viewed)}, view property details`;

export const mapClusterLabel = (cluster: MapClusterPin) => {
  if (cluster.count === 1) return '1 home, reveal price pin';
  const visibleCount = formatClusterCount(cluster.count);
  const exactCount = cluster.count.toLocaleString('en-US');
  const count = visibleCount === exactCount ? exactCount : `${visibleCount}, ${exactCount}`;
  return `${count} homes, zoom in`;
};

const PricePinBubble = ({ pin, variant }: { pin: MapPin; variant: PinVariant }) => {
  const metrics = usePinLabelMetrics();
  const markup = useMemo(
    () => ({ __html: pricePinMarkup(pin, variant, 'globe', metrics) }),
    [pin, variant, metrics],
  );
  return <Box aria-hidden className="pointer-events-none" dangerouslySetInnerHTML={markup} />;
};

// Puts the tail tip on the marker's origin, matching PIN_ANCHOR.
const PRICE_PIN_CLASS = 'relative w-max -translate-x-1/2 -translate-y-[38.57px]';

/** Globe marker version of the price pin; the street map draws the same markup imperatively. */
export const PricePinArtwork = ({
  pin,
  variant,
  selected = false,
  viewed = false,
  onSelect,
}: {
  pin: MapPin;
  variant: PinVariant;
  selected?: boolean;
  viewed?: boolean;
  onSelect?: (pin: MapPin, origin: HTMLElement) => void;
}) => {
  useInsertionEffect(ensureArtworkStyles, []);
  const [hovered, setHovered] = useState(false);
  const handleSelectClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onSelect?.(pin, event.currentTarget);
    },
    [onSelect, pin],
  );
  const handlePointerEnter = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (onSelect && event.pointerType !== 'touch') setHovered(true);
    },
    [onSelect],
  );
  const handlePointerLeave = useCallback(() => setHovered(false), []);
  const showHover = hovered && onSelect !== undefined;
  return (
    <Box
      role={onSelect ? undefined : 'img'}
      aria-label={onSelect ? undefined : mapPinLabel(pin, viewed)}
      className={
        selected || showHover ? `${PRICE_PIN_CLASS} ${PIN_SELECTED_CLASS}` : PRICE_PIN_CLASS
      }
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <PricePinBubble pin={pin} variant={variant} />
      {onSelect && (
        <Button
          aria-label={mapPinButtonLabel(pin, viewed)}
          analyticsName="map-refresh-price-pin"
          variant="secondary"
          onClick={handleSelectClick}
          className="absolute inset-0 size-full rounded-xl border-0 bg-transparent p-0 shadow-none not-disabled:hover:bg-transparent not-disabled:active:bg-transparent"
        />
      )}
    </Box>
  );
};

/** Globe marker version of the count bubble. */
export const ClusterPinArtwork = ({
  cluster,
  shape = 'pin',
  onClick,
}: {
  cluster: MapClusterPin;
  shape?: ClusterShape;
  onClick: (cluster: MapClusterPin) => void;
}) => {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClick(cluster);
    },
    [cluster, onClick],
  );
  const markup = useMemo(
    () => ({ __html: clusterMarkup(cluster, 'globe', shape) }),
    [cluster, shape],
  );
  return (
    <Box
      aria-label={mapClusterLabel(cluster)}
      onClick={handleClick}
      className="relative block cursor-pointer border-0 bg-transparent p-0"
      dangerouslySetInnerHTML={markup}
    />
  );
};
